import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Nav from "./components/Nav.jsx";
import Footer from "./components/Footer.jsx";
import PreviewBanner from "./components/PreviewBanner.jsx";

import Home from "./pages/Home.jsx";
import Invest from "./pages/Invest.jsx";
import Borrow from "./pages/Borrow.jsx";
import Wealth from "./pages/Wealth.jsx";
import Group from "./pages/Group.jsx";
import Transactions from "./pages/Transactions.jsx";
import Funds from "./pages/Funds.jsx";
import FundDetail from "./pages/FundDetail.jsx";
import Team from "./pages/Team.jsx";
import Contact from "./pages/Contact.jsx";
import DataRoom from "./pages/DataRoom.jsx";
import Login from "./pages/Login.jsx";
import Legal from "./pages/Legal.jsx";
import Brief from "./pages/Brief.jsx";
import Unsubscribe from "./pages/Unsubscribe.jsx";
import Privacy from "./pages/Privacy.jsx";
import Products from "./pages/Products.jsx";
import Faqs from "./pages/Faqs.jsx";
import NotFound from "./pages/NotFound.jsx";
import PolicyPage from "./pages/PolicyPage.jsx";
import Policies from "./pages/Policies.jsx";
import InvestorGate from "./components/InvestorGate.jsx";

import PortalLayout from "./pages/portal/PortalLayout.jsx";
import PortalDashboard from "./pages/portal/PortalDashboard.jsx";
import PortalFundDetail from "./pages/portal/PortalFundDetail.jsx";
import PortalDocuments from "./pages/portal/PortalDocuments.jsx";
import PortalAccount from "./pages/portal/PortalAccount.jsx";
import PortalPasskeys from "./pages/portal/PortalPasskeys.jsx";
import PortalPipeline from "./pages/portal/PortalPipeline.jsx";
import PortalDealDetail from "./pages/portal/PortalDealDetail.jsx";
import PortalDealNda from "./pages/portal/PortalDealNda.jsx";
import PortalKyc from "./pages/portal/PortalKyc.jsx";
import PortalKycStatus from "./pages/portal/PortalKycStatus.jsx";

// React Router does not honour #fragment links on its own. Without this,
// /legal#privacy (and every other footer anchor) lands at the top of the page.
function useHashScroll(location) {
  useEffect(() => {
    const id = location.hash ? decodeURIComponent(location.hash.slice(1)) : "";
    if (!id) {
      window.scrollTo(0, 0);
      return;
    }
    // Wait for the routed page to mount before looking for the target.
    let tries = 0;
    const find = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (tries++ < 10) {
        requestAnimationFrame(find);
      }
    };
    requestAnimationFrame(find);
  }, [location.pathname, location.hash]);
}

function ConditionalChrome({ children }) {
  const location = useLocation();
  useHashScroll(location);
  // Login + portal pages render without the marketing Nav/Footer.
  const isLogin  = location.pathname === "/login";
  const isPortal = location.pathname.startsWith("/portal");
  const naked    = isLogin || isPortal;
  return (
    <>
      <a href="#main" className="skip-link">Skip to main content</a>
      {!naked && <PreviewBanner />}
      {!naked && <Nav />}
      <main id="main" tabIndex={-1}>{children}</main>
      {!naked && <Footer />}
    </>
  );
}

export default function App() {
  // Host-based root: invest.daksfirst.com lands on the Invest page;
  // daksfirst.com (and all other hosts) land on Home. Same SPA, one project.
  const isInvestHost =
    typeof window !== "undefined" &&
    window.location.hostname.startsWith("invest.");

  return (
    <ConditionalChrome>
      <Routes>
        <Route path="/"             element={isInvestHost ? <InvestorGate><Invest /></InvestorGate> : <Home />} />
        <Route path="/invest"       element={<InvestorGate><Invest /></InvestorGate>} />
        <Route path="/borrow"       element={<Borrow />} />
        <Route path="/wealth"       element={<Wealth />} />
        <Route path="/group"        element={<Group />} />
        <Route path="/transactions" element={<Transactions />} />
        <Route path="/funds"        element={<InvestorGate><Funds /></InvestorGate>} />
        <Route path="/funds/:slug"  element={<InvestorGate><FundDetail /></InvestorGate>} />
        <Route path="/team"         element={<Team />} />
        <Route path="/contact"      element={<Contact />} />
        <Route path="/data-room"    element={<DataRoom />} />
        <Route path="/login"        element={<Login />} />
        <Route path="/legal"        element={<Legal />} />
        <Route path="/brief"        element={<Brief />} />
        <Route path="/unsubscribe"  element={<Unsubscribe />} />
        <Route path="/privacy"      element={<Privacy />} />
        <Route path="/uk/lending-products" element={<Products />} />
        <Route path="/faqs"         element={<Faqs />} />
        <Route path="/faq"          element={<Faqs />} />

        {/* POLICIES - content in src/data/policies, one route per policy */}
        <Route path="/policies"               element={<Policies />} />
        <Route path="/complaints"             element={<PolicyPage slug="complaints" />} />
        <Route path="/financial-difficulty"   element={<PolicyPage slug="financial-difficulty" />} />
        <Route path="/tariff"                 element={<PolicyPage slug="tariff" />} />
        <Route path="/vulnerable-customers"   element={<PolicyPage slug="vulnerable-customers" />} />
        <Route path="/fair-treatment"         element={<PolicyPage slug="fair-treatment" />} />
        <Route path="/regulatory-information" element={<PolicyPage slug="regulatory-information" />} />
        <Route path="/investor-access"        element={<PolicyPage slug="investor-access" />} />
        <Route path="/intermediaries/terms"   element={<PolicyPage slug="intermediaries/terms" />} />
        <Route path="/financial-crime"        element={<PolicyPage slug="financial-crime" />} />
        <Route path="/whistleblowing"         element={<PolicyPage slug="whistleblowing" />} />
        <Route path="/modern-slavery"         element={<PolicyPage slug="modern-slavery" />} />
        <Route path="/accessibility"          element={<PolicyPage slug="accessibility" />} />
        <Route path="/data-room-terms"        element={<PolicyPage slug="data-room-terms" />} />

        {/* INVESTOR PORTAL — auth-gated inside PortalLayout */}
        <Route path="/portal" element={<PortalLayout />}>
          <Route index                      element={<PortalDashboard />} />
          <Route path="funds/:slug"         element={<PortalFundDetail />} />
          <Route path="documents"           element={<PortalDocuments />} />
          <Route path="account"             element={<PortalAccount />} />
          <Route path="account/passkeys"    element={<PortalPasskeys />} />
          <Route path="pipeline"            element={<PortalPipeline />} />
          <Route path="pipeline/:slug"      element={<PortalDealDetail />} />
          <Route path="pipeline/:slug/nda"  element={<PortalDealNda />} />
          <Route path="onboarding/kyc"      element={<PortalKyc />} />
          <Route path="onboarding/status"   element={<PortalKycStatus />} />
        </Route>

        {/* retired routes - redirect for any inbound links */}
        <Route path="/credit"       element={<Navigate to="/invest" replace />} />
        <Route path="/partner"      element={<Navigate to="/invest" replace />} />
        <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
        <Route path="/legal/privacy"  element={<Navigate to="/privacy" replace />} />
        <Route path="*"             element={<NotFound />} />
      </Routes>
    </ConditionalChrome>
  );
}
